/**
 * CheckoutDialog
 *
 * Modal dialog for purchasing credits. Supports two payment modes:
 * 1. Select a previously saved payment method
 * 2. Enter a new credit card inline (with option to save it)
 *
 * On success, shows a celebratory popup. On error, shows inline alert.
 *
 * Requirements: 6.1, 6.3, 6.7, 11.4
 */

import { useState, useEffect, useCallback } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Typography, Select, MenuItem, CircularProgress,
    Box, IconButton, FormControlLabel, Checkbox, TextField,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CelebrationIcon from '@mui/icons-material/Celebration';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useStripe, useElements, CardNumberElement, CardExpiryElement, CardCvcElement } from '@stripe/react-stripe-js';
import confetti from 'canvas-confetti';
import { PaymentService } from '../services/payment-service';
import { PaymentMethodsPage } from '../pages/PaymentMethodsPage';
import { useNotification } from '../contexts/NotificationContext';
import type { PaymentMethod } from '../types/payment';

export interface CheckoutTier { id: string; name: string; price: number; credits: number; }
export interface CheckoutDialogProps { open: boolean; onClose: () => void; tier: CheckoutTier | null; onSuccess?: () => void; }

const paymentService = new PaymentService();

const TIER_COLORS: Record<string, string> = {
    Free: 'rgba(255,255,255,0.85)', Basic: '#64b5f6', Plus: '#ce93d8', Pro: '#ffd54f',
};

const STRIPE_ELEMENT_STYLE = {
    base: { fontSize: '16px', color: '#e0e0e0', fontFamily: '"Inter","Roboto","Helvetica","Arial",sans-serif', '::placeholder': { color: '#666' } },
    invalid: { color: '#f44336' },
};

const stripeFieldBox = {
    border: '1px solid rgba(255,255,255,0.23)', borderRadius: 1, px: 1.5, py: 1.5,
    '&:hover': { borderColor: 'rgba(255,255,255,0.4)' },
    '&:focus-within': { borderColor: 'primary.main', boxShadow: '0 0 0 2px rgba(33,150,243,0.25)' },
    transition: 'border-color 0.2s, box-shadow 0.2s',
};

export function CheckoutDialog({ open, onClose, tier, onSuccess }: CheckoutDialogProps): JSX.Element {
    const stripe = useStripe();
    const elements = useElements();
    const { notify } = useNotification();

    // Saved methods state
    const [methods, setMethods] = useState<PaymentMethod[]>([]);
    const [selectedMethodId, setSelectedMethodId] = useState('');
    const [isLoadingMethods, setIsLoadingMethods] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [manageOpen, setManageOpen] = useState(false);

    // Payment mode is determined by whether saved methods exist
    const [useNewCard, setUseNewCard] = useState(false);

    // New card state
    const [cardholderName, setCardholderName] = useState('');
    const [cardNameError, setCardNameError] = useState('');
    const [saveCard, setSaveCard] = useState(true);
    const [cardFieldsComplete, setCardFieldsComplete] = useState({ number: false, expiry: false, cvc: false });

    // Success celebration state
    const [successOpen, setSuccessOpen] = useState(false);
    const [successCredits, setSuccessCredits] = useState(0);

    // Fire confetti when success dialog opens
    useEffect(() => {
        if (!successOpen) return;
        const colors = ['#ff0000', '#ff6600', '#ffdd00', '#00ff00', '#00ccff', '#6633ff', '#ff33cc', '#ffffff', '#4caf50', '#2196f3'];
        const canvas = document.querySelector('canvas.confetti-canvas') as HTMLCanvasElement | null;
        if (canvas) canvas.style.zIndex = '1400';
        const opts = { colors, scalar: 1.4, ticks: 200, zIndex: 1400 };
        confetti({ ...opts, particleCount: 200, spread: 100, origin: { x: 0.5, y: 0.5 }, startVelocity: 45 });
        confetti({ ...opts, particleCount: 80, angle: 60, spread: 70, origin: { x: 0, y: 0.65 }, startVelocity: 35 });
        confetti({ ...opts, particleCount: 80, angle: 120, spread: 70, origin: { x: 1, y: 0.65 }, startVelocity: 35 });
    }, [successOpen]);

    const fetchMethods = useCallback(async () => {
        setIsLoadingMethods(true);
        try {
            const data = await paymentService.listPaymentMethods();
            setMethods(data);
            const def = data.find((m) => m.is_default);
            setSelectedMethodId(def?.payment_method_id ?? data[0]?.payment_method_id ?? '');
            // If no saved methods, show new card form
            if (data.length === 0) setUseNewCard(true);
            else setUseNewCard(false);
        } catch {
            notify({ message: 'Failed to load payment methods', severity: 'error' });
        } finally {
            setIsLoadingMethods(false);
        }
    }, [notify]);

    useEffect(() => {
        if (open) {
            fetchMethods();
            setCardholderName('');
            setCardNameError('');
            setCardFieldsComplete({ number: false, expiry: false, cvc: false });
        }
    }, [open, fetchMethods]);

    useEffect(() => { if (!open) setIsProcessing(false); }, [open]);

    const handleManageClose = useCallback(() => { setManageOpen(false); fetchMethods(); }, [fetchMethods]);

    // Process payment with a new card
    const handleNewCardPurchase = useCallback(async () => {
        if (!tier || !stripe || !elements) return;

        // Validate cardholder name
        if (!cardholderName.trim()) {
            setCardNameError('Cardholder name is required');
            return;
        }
        setCardNameError('');

        // Validate card fields
        if (!cardFieldsComplete.number || !cardFieldsComplete.expiry || !cardFieldsComplete.cvc) {
            notify({ message: 'Please complete all card fields', severity: 'error' });
            return;
        }

        setIsProcessing(true);
        try {
            const cardNumber = elements.getElement(CardNumberElement);
            if (!cardNumber) { setIsProcessing(false); return; }

            // Create payment method from card elements
            const { error: pmError, paymentMethod: pm } = await stripe.createPaymentMethod({
                type: 'card',
                card: cardNumber,
                billing_details: { name: cardholderName.trim() },
            });

            if (pmError || !pm) {
                notify({ message: pmError?.message || 'Failed to process card', severity: 'error' });
                setIsProcessing(false);
                return;
            }

            // Ensure Stripe customer exists before processing payment
            try {
                await paymentService.createCustomer();
            } catch (custErr) {
                notify({ message: 'Failed to set up payment account. Please try again.', severity: 'error' });
                setIsProcessing(false);
                return;
            }

            // Save the card for future use if requested
            if (saveCard) {
                try {
                    await paymentService.addPaymentMethod(pm.id);
                } catch {
                    // Non-blocking — card save failed but we can still process payment
                }
            }

            // Create payment with the new payment method
            const response = await paymentService.createPayment(tier.id, pm.id);

            if (response.requires_action && response.client_secret) {
                const { error: confirmError } = await stripe.confirmPayment({ clientSecret: response.client_secret, redirect: 'if_required' });
                if (confirmError) { notify({ message: confirmError.message || 'Authentication failed.', severity: 'error' }); setIsProcessing(false); return; }
                setSuccessCredits(tier.credits);
                setSuccessOpen(true);
                onSuccess?.();
                onClose();
                return;
            }

            if (response.success) {
                setSuccessCredits(response.credits ?? tier.credits);
                setSuccessOpen(true);
                onSuccess?.();
                onClose();
                return;
            }

            notify({ message: response.error || 'Payment failed. Please try again.', severity: 'error' });
        } catch (err) {
            notify({ message: err instanceof Error ? err.message : 'Payment failed. Please try again.', severity: 'error' });
        } finally {
            setIsProcessing(false);
        }
    }, [tier, stripe, elements, cardholderName, cardFieldsComplete, saveCard, onClose, onSuccess, notify]);

    // Process payment with saved card
    const handleSavedCardPurchase = useCallback(async () => {
        if (!tier || !selectedMethodId) return;
        setIsProcessing(true);
        try {
            const response = await paymentService.createPayment(tier.id, selectedMethodId);

            if (response.requires_action && response.client_secret) {
                if (!stripe) { notify({ message: 'Stripe is not loaded. Please try again.', severity: 'error' }); setIsProcessing(false); return; }
                const { error: confirmError } = await stripe.confirmPayment({ clientSecret: response.client_secret, redirect: 'if_required' });
                if (confirmError) { notify({ message: confirmError.message || 'Authentication failed.', severity: 'error' }); setIsProcessing(false); return; }
                setSuccessCredits(tier.credits);
                setSuccessOpen(true);
                onSuccess?.();
                onClose();
                return;
            }

            if (response.success) {
                setSuccessCredits(response.credits ?? tier.credits);
                setSuccessOpen(true);
                onSuccess?.();
                onClose();
                return;
            }

            notify({ message: response.error || 'Payment failed. Please try again.', severity: 'error' });
        } catch (err) {
            notify({ message: err instanceof Error ? err.message : 'Payment failed. Please try again.', severity: 'error' });
        } finally {
            setIsProcessing(false);
        }
    }, [tier, selectedMethodId, stripe, onClose, onSuccess, notify]);

    const handleConfirmPurchase = useCallback(() => {
        if (useNewCard) {
            handleNewCardPurchase();
        } else {
            handleSavedCardPurchase();
        }
    }, [useNewCard, handleNewCardPurchase, handleSavedCardPurchase]);

    const canConfirm = useNewCard
        ? cardFieldsComplete.number && cardFieldsComplete.expiry && cardFieldsComplete.cvc && !!cardholderName.trim()
        : !!selectedMethodId && methods.length > 0;

    return (
        <>
            {/* ── Full-screen processing overlay ── */}
            {isProcessing && (
                <Box sx={(theme) => ({
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                    bgcolor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.9)' : 'rgba(255,255,255,0.95)',
                    zIndex: 1400,
                })}>
                    <CircularProgress size={48} sx={{ mb: 3 }} />
                    <Typography variant="h6" fontWeight={600}>
                        Processing Payment...
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                        Please do not close this window or navigate away.
                    </Typography>
                </Box>
            )}

            {/* ── Checkout Dialog ── */}
            <Dialog open={open} onClose={isProcessing ? undefined : onClose} aria-labelledby="checkout-dialog-title" maxWidth="xs" fullWidth disableEnforceFocus>
                <DialogTitle id="checkout-dialog-title">Confirm Purchase</DialogTitle>
                <DialogContent dividers sx={{ position: 'relative' }}>
                    {tier && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {/* Tier info card */}
                            <Box data-testid="checkout-tier-info" sx={{
                                background: 'linear-gradient(135deg, rgba(33,150,243,0.08) 0%, rgba(156,39,176,0.08) 100%)',
                                border: '1px solid rgba(255,255,255,0.1)',
                                borderRadius: 2, p: 2, textAlign: 'center',
                            }}>
                                <Typography variant="subtitle1" fontWeight={800} letterSpacing={2} sx={{
                                    color: TIER_COLORS[tier.name] || 'rgba(255,255,255,0.85)',
                                    textTransform: 'uppercase',
                                }}>
                                    {tier.name}
                                </Typography>
                                <Typography variant="h4" fontWeight={800} sx={{ my: 0.5 }}>
                                    ${tier.price}
                                </Typography>
                                <Typography variant="body1" sx={{ color: '#4caf50', fontWeight: 600 }}>
                                    {tier.credits} credits
                                </Typography>
                            </Box>

                            {/* Payment method section */}
                            <Box>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>Payment Method</Typography>

                                {isLoadingMethods ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={24} /></Box>
                                ) : !useNewCard ? (
                                    /* Saved card selection */
                                    <>
                                        <Select value={selectedMethodId} onChange={(e: SelectChangeEvent) => setSelectedMethodId(e.target.value)}
                                            fullWidth size="small" disabled={isProcessing} aria-label="Select payment method" data-testid="payment-method-select">
                                            {methods.map((m) => (
                                                <MenuItem key={m.payment_method_id} value={m.payment_method_id}>
                                                    {m.card_brand.charAt(0).toUpperCase() + m.card_brand.slice(1)} •••• {m.last_four}{m.is_default ? ' (default)' : ''}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                            <Button size="small" onClick={() => setUseNewCard(true)} disabled={isProcessing}
                                                sx={{ textTransform: 'none' }} data-testid="use-new-card-button">
                                                Use a New Card
                                            </Button>
                                            <Button size="small" onClick={() => setManageOpen(true)} disabled={isProcessing}
                                                sx={{ textTransform: 'none' }} data-testid="manage-payment-methods-button">
                                                Manage Payment Methods
                                            </Button>
                                        </Box>
                                    </>
                                ) : (
                                    /* New card entry form — unmount when manage dialog is open to avoid duplicate Stripe elements */
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                        {!manageOpen && (
                                            <>
                                                <TextField
                                                    label="Cardholder Name"
                                                    value={cardholderName}
                                                    onChange={(e) => { setCardholderName(e.target.value); setCardNameError(''); }}
                                                    error={!!cardNameError}
                                                    helperText={cardNameError}
                                                    size="small"
                                                    fullWidth
                                                    disabled={isProcessing}
                                                    data-testid="cardholder-name-input"
                                                />
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                                                        Card Number
                                                    </Typography>
                                                    <Box sx={stripeFieldBox}>
                                                        <CardNumberElement
                                                            options={{ style: STRIPE_ELEMENT_STYLE }}
                                                            onChange={(e) => setCardFieldsComplete(prev => ({ ...prev, number: e.complete }))}
                                                        />
                                                    </Box>
                                                </Box>
                                                <Box sx={{ display: 'flex', gap: 1.5 }}>
                                                    <Box sx={{ flex: 1 }}>
                                                        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                                                            Expiry
                                                        </Typography>
                                                        <Box sx={stripeFieldBox}>
                                                            <CardExpiryElement
                                                                options={{ style: STRIPE_ELEMENT_STYLE }}
                                                                onChange={(e) => setCardFieldsComplete(prev => ({ ...prev, expiry: e.complete }))}
                                                            />
                                                        </Box>
                                                    </Box>
                                                    <Box sx={{ flex: 1 }}>
                                                        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                                                            CVC
                                                        </Typography>
                                                        <Box sx={stripeFieldBox}>
                                                            <CardCvcElement
                                                                options={{ style: STRIPE_ELEMENT_STYLE }}
                                                                onChange={(e) => setCardFieldsComplete(prev => ({ ...prev, cvc: e.complete }))}
                                                            />
                                                        </Box>
                                                    </Box>
                                                </Box>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={saveCard}
                                                            onChange={(e) => setSaveCard(e.target.checked)}
                                                            size="small"
                                                            disabled={isProcessing}
                                                            data-testid="save-card-checkbox"
                                                        />
                                                    }
                                                    label={<Typography variant="body2">Save this card for future purchases</Typography>}
                                                />
                                            </>
                                        )}
                                        {manageOpen && (
                                            <Typography variant="body2" color="text.secondary">
                                                Close the Manage Payment Methods dialog to continue entering card details.
                                            </Typography>
                                        )}
                                        {methods.length > 0 && (
                                            <Button size="small" onClick={() => setUseNewCard(false)} disabled={isProcessing}
                                                sx={{ textTransform: 'none', alignSelf: 'flex-start' }} data-testid="use-saved-card-button">
                                                Use a Saved Card Instead
                                            </Button>
                                        )}
                                        <Button size="small" onClick={() => setManageOpen(true)} disabled={isProcessing}
                                            sx={{ textTransform: 'none', alignSelf: 'flex-start' }} data-testid="manage-payment-methods-button-new">
                                            Manage Payment Methods
                                        </Button>
                                    </Box>
                                )}

                                <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1.5 }}>
                                    🔒 Secure checkout powered by
                                    <img src={new URL('../assets/stripe-logo.svg', import.meta.url).href} alt="Stripe" style={{ height: 28, marginLeft: 1, display: 'block' }} />
                                </Typography>
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose} disabled={isProcessing}>Cancel</Button>
                    <Button variant="contained" onClick={handleConfirmPurchase}
                        disabled={isProcessing || !canConfirm}
                        data-testid="confirm-purchase-button" sx={{ textTransform: 'none' }}>
                        {isProcessing ? <><CircularProgress size={18} sx={{ mr: 1, color: 'inherit' }} />Processing…</> : 'Confirm Purchase'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Manage Payment Methods Dialog ── */}
            <Dialog open={manageOpen} onClose={handleManageClose} maxWidth={false} disableEnforceFocus
                sx={{ '& .MuiDialog-paper': { maxWidth: 700, width: '100%', maxHeight: '85vh' } }}
                data-testid="manage-methods-dialog">
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    Manage Payment Methods
                    <IconButton onClick={handleManageClose} size="small"><CloseIcon /></IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ p: 0 }}><PaymentMethodsPage vertical /></DialogContent>
            </Dialog>

            {/* ── Success Celebration Dialog ── */}
            <Dialog open={successOpen} onClose={() => setSuccessOpen(false)} maxWidth="xs" fullWidth disableEnforceFocus
                PaperProps={{
                    sx: (theme) => ({
                        background: theme.palette.mode === 'dark'
                            ? 'linear-gradient(135deg, #1a2a1a 0%, #1a1a2e 50%, #2a1a2a 100%)'
                            : 'linear-gradient(135deg, #f0fff0 0%, #f0f0ff 50%, #fff0ff 100%)',
                        border: '2px solid rgba(76,175,80,0.5)',
                        borderRadius: 3,
                        textAlign: 'center',
                        overflow: 'visible',
                        boxShadow: '0 0 40px rgba(76,175,80,0.2), 0 0 80px rgba(33,150,243,0.1)',
                        animation: 'bounceIn 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)',
                    })
                }}>
                <DialogContent sx={{ py: 5, px: 4 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 3 }}>
                        <AutoAwesomeIcon sx={{ fontSize: 36, color: '#ffd54f', animation: 'pulse 1.5s infinite' }} />
                        <CelebrationIcon sx={{ fontSize: 48, color: '#4caf50' }} />
                        <AutoAwesomeIcon sx={{ fontSize: 36, color: '#ce93d8', animation: 'pulse 1.5s infinite 0.5s' }} />
                    </Box>
                    <Typography variant="h5" fontWeight={700} sx={{ mb: 3, color: '#4caf50' }}>
                        Purchase Successful!
                    </Typography>
                    <Typography variant="h6" sx={{ mb: 3, color: '#4caf50' }}>
                        + {successCredits.toLocaleString()} Credits
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        Thank you for being a valued customer
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
                    <Button variant="contained" onClick={() => setSuccessOpen(false)}
                        sx={{
                            textTransform: 'none', fontWeight: 600, px: 4, borderRadius: 2,
                            background: 'linear-gradient(135deg, #4caf50, #2196f3)',
                            '&:hover': { background: 'linear-gradient(135deg, #43a047, #1e88e5)' },
                        }}>
                        Continue
                    </Button>
                </DialogActions>
            </Dialog>

            <style>{`
                @keyframes pulse {
                    0%, 100% { opacity: 1; transform: scale(1); }
                    50% { opacity: 0.5; transform: scale(1.2); }
                }
                @keyframes bounceIn {
                    0% { opacity: 0; transform: scale(0.3); }
                    50% { opacity: 1; transform: scale(1.05); }
                    70% { transform: scale(0.95); }
                    100% { transform: scale(1); }
                }
            `}</style>
        </>
    );
}
